import { Plate } from '../components/Card';
import React, { useEffect, useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Cell, LineChart, Line, Legend
} from 'recharts';
import api from '../api';

const fmt = v => new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB', maximumFractionDigits: 0 }).format(v || 0);
const fmtCash = v => {
  const abs = new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB', maximumFractionDigits: 0 }).format(Math.abs(v || 0));
  if ((v || 0) > 0) return '+' + abs;
  if ((v || 0) < 0) return '−' + abs;
  return abs;
};
const cashColor = v => (v || 0) > 0 ? '#0e9f6e' : (v || 0) < 0 ? '#e04848' : 'var(--text2)';

/* Прогноз на конец месяца на основе текущего темпа */
function getProjection(revenue, period) {
  if (!period || !revenue) return null;
  const [year, month] = period.split('-').map(Number);
  const daysInMonth = new Date(year, month, 0).getDate();
  const now = new Date();
  const isCurrentMonth = year === now.getFullYear() && month === (now.getMonth() + 1);
  if (!isCurrentMonth) return null; // прошлые месяцы — прогноз не нужен
  const daysPassed = now.getDate();
  if (daysPassed === 0) return null;
  const dailyAvg = revenue / daysPassed;
  const projection = Math.round(dailyAvg * daysInMonth);
  const daysLeft = daysInMonth - daysPassed;
  const growth = projection - revenue;
  return { projection, dailyAvg, daysPassed, daysInMonth, daysLeft, growth };
}
const fmtShort = v => {
  if (v >= 1000000) return (v / 1000000).toFixed(1) + 'M';
  if (v >= 1000) return (v / 1000).toFixed(0) + 'K';
  return Math.round(v);
};
const COLORS = ['#00a7dc','#0e9f6e','#e08a00','#e04848','#00a7dc','#00a7dc','#00a7dc','#0e9f6e','#e08a00','#0e9f6e','#00a7dc','#e08a00','#00a7dc','#0e9f6e','#e04848'];
const PINNED_RETAIL_STORES = [
  { key: 'северный', rank: 0 },
  { key: 'жукова', rank: 1 },
  { key: 'мартынов', rank: 2 },
];

const getPinnedRetailStoreRank = store => {
  const name = (store?.name || '').toLowerCase();
  const match = PINNED_RETAIL_STORES.find(item => name.includes(item.key));
  return match ? match.rank : -1;
};

const getPrevPeriod = (periods, period) => {
  const idx = periods.indexOf(period);
  return idx >= 0 && idx < periods.length - 1 ? periods[idx + 1] : null;
};

const getDailyStatsDays = period => {
  if (!period) return 30;
  const [year, month] = period.split('-').map(Number);
  if (!year || !month) return 30;
  const periodStart = new Date(year, month - 1, 1);
  const now = new Date();
  const diffDays = Math.ceil((now - periodStart) / (24 * 60 * 60 * 1000)) + 2;
  return Math.max(30, Math.min(diffDays, 180));
};

const MonthArrows = ({ period, periods, onChange }) => {
  const idx = periods.indexOf(period);
  const canOlder = idx >= 0 && idx < periods.length - 1;
  const canNewer = idx > 0;
  const buttonStyle = enabled => ({
    width: 22, height: 22, borderRadius: 7, border: '1px solid var(--border)',
    background: enabled ? 'rgba(0,167,220,0.15)' : 'transparent',
    color: enabled ? '#00a7dc' : 'var(--text2)',
    cursor: enabled ? 'pointer' : 'default',
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
    fontSize: 11, lineHeight: 1,
  });
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 2 }}>
      <button type="button" onClick={() => canOlder && onChange(periods[idx + 1])} disabled={!canOlder} style={buttonStyle(canOlder)}>◀</button>
      <span style={{ fontSize: 11, color: 'var(--text2)', whiteSpace: 'nowrap' }}>Пред. месяц</span>
      <button type="button" onClick={() => canNewer && onChange(periods[idx - 1])} disabled={!canNewer} style={buttonStyle(canNewer)}>▶</button>
    </div>
  );
};

const Tab = ({ label, active, onClick }) => (
  <button onClick={onClick} style={{
    padding: '8px 20px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: active ? 700 : 400,
    background: active ? 'var(--accent)' : 'var(--bg3)', color: active ? '#fff' : 'var(--text2)',
    transition: 'all 0.15s',
  }}>{label}</button>
);

const Card = ({ label, value, sub, color = 'var(--accent)', icon }) => (
  <div style={{ background: 'var(--bg3)', borderRadius: 14, padding: '16px 18px 18px', flex: 1, minWidth: 160, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: 4 }}>
    <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)' }}>{icon} {label}</div>
    <div style={{ fontSize: 24, fontWeight: 800, color, lineHeight: 1.15, whiteSpace: 'nowrap' }}>{value}</div>
    <div style={{ fontSize: 11, color: 'var(--text2)', minHeight: 16 }}>{sub || ''}</div>
  </div>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 14px', fontSize: 12, maxWidth: 260 }}>
      <div style={{ fontWeight: 600, marginBottom: 6, wordBreak: 'break-word' }}>{label}</div>
      {payload.map(p => (
        <div key={p.dataKey} style={{ color: p.color }}>{p.name}: {fmt(p.value)}</div>
      ))}
    </div>
  );
};

export default function Retail() {
  const [tab, setTab] = useState('stores');
  const [expTabFilter, setExpTabFilter] = useState('month');
  const [summary, setSummary] = useState(null);
  const [stores, setStores] = useState([]);
  const [daily, setDaily] = useState([]);
  const [tinkoffStats, setTinkoffStats] = useState(null);
  const [tinkoffDaily, setTinkoffDaily] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [period, setPeriod] = useState('');
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    api.get('/retail/stores/periods').then(r => {
      const ps = r.data;
      setPeriods(ps);
      if (ps.length) setPeriod(ps[0]);
    });
  }, []);

  const [prevStoreMap, setPrevStoreMap] = useState({});
  const [prevSummary, setPrevSummary] = useState(null);
  const [dailyStats, setDailyStats] = useState({});
  const [storeDayIdx, setStoreDayIdx] = useState({});
  const [expenses, setExpenses] = useState({});
  const [receipts, setReceipts] = useState([]);
  const [receiptsLoading, setReceiptsLoading] = useState(false);
  const [expenseStoreFilter, setExpenseStoreFilter] = useState('all');
  const [receiptStoreFilter, setReceiptStoreFilter] = useState('all');
  const [expandedExpenses, setExpandedExpenses] = useState({});
  const [expFilter, setExpFilter] = useState({});  // per-store: {type: 'today'|'date'|'month', date: str, month: str}
  const [storeHistory, setStoreHistory] = useState({});
  const [storeCardPeriods, setStoreCardPeriods] = useState({});

  useEffect(() => {
    if (!periods.length) return;
    Promise.all(
      periods.slice(0, 12).map(p =>
        api.get(`/retail/stores?period=${p}&store_type=own`)
          .then(r => [p, r.data || []])
          .catch(() => [p, []])
      )
    ).then(rows => {
      const next = {};
      rows.forEach(([p, items]) => {
        next[p] = {};
        items.forEach(item => { next[p][item.store_external_id] = item; });
      });
      setStoreHistory(next);
    });
  }, [periods]);

  useEffect(() => {
    if (!period) return;
    setLoading(true);
    setDailyStats({});
    setExpenses({});
    const [y, m] = period.split('-').map(Number);
    const prevPeriod = m === 1 ? `${y - 1}-12` : `${y}-${String(m - 1).padStart(2, '0')}`;
    const now = new Date();
    const isCurrentMonth = y === now.getFullYear() && m === (now.getMonth() + 1);

    // Fast: main data — unblocks the UI immediately
    Promise.all([
      api.get(`/retail/stores/summary?period=${period}&store_type=own`).then(r => r.data).catch(() => null),
      api.get(`/retail/stores?period=${period}&store_type=own`).then(r => r.data).catch(() => []),
      api.get('/retail/stats?days=120').then(r => r.data).catch(() => null),
      api.get('/retail/daily?days=120').then(r => r.data).catch(() => []),
      api.get(`/retail/stores?period=${prevPeriod}&store_type=own`).then(r => r.data).catch(() => []),
      api.get(`/retail/stores/summary?period=${prevPeriod}&store_type=own`).then(r => r.data).catch(() => null),
    ]).then(([sum, st, ts, td, prevSt, prevSum]) => {
      setSummary(sum);
      setStores(st || []);
      setStoreHistory(prev => ({
        ...prev,
        [period]: Object.fromEntries((st || []).map(item => [item.store_external_id, item])),
        [prevPeriod]: Object.fromEntries((prevSt || []).map(item => [item.store_external_id, item])),
      }));
      setTinkoffStats(ts);
      setTinkoffDaily((td || []).map(d => ({ ...d, label: d.date.slice(5) })));
      setPrevSummary(prevSum);
      const map = {};
      prevSt.forEach(ps => { map[ps.store_external_id] = ps; });
      setPrevStoreMap(map);
    }).finally(() => setLoading(false));

    // Slow: daily stats and expenses — load in background without blocking UI
    api.get(`/retail/own-daily-stats?days=${getDailyStatsDays(period)}`, { timeout: 180000 }).then(r => {
      const rawDaily = r.data || {};
      const daily = {};
      Object.entries(rawDaily).forEach(([sid, sd]) => {
        daily[sid] = {
          ...sd,
          days: (sd.days || []).filter(d => d.date?.startsWith(period)),
        };
      });
      setDailyStats(daily);
      const today = new Date().toISOString().slice(0, 10);
      const idxMap = {};
      Object.entries(daily).forEach(([sid, sd]) => {
        const days = sd.days || [];
        const ti = isCurrentMonth ? days.findIndex(d => d.date === today) : -1;
        idxMap[sid] = ti >= 0 ? ti : Math.max(days.length - 1, 0);
      });
      setStoreDayIdx(idxMap);
    }).catch(() => {});

    api.get('/retail/own-expenses', { timeout: 60000 }).then(r => {
      setExpenses(r.data || {});
    }).catch(() => {});
  }, [period]);

  useEffect(() => {
    if (!period || tab !== 'receipts') return;
    setReceiptsLoading(true);
    api.get(`/retail/own-receipts?period=${period}`, { timeout: 180000 })
      .then(r => setReceipts(r.data || []))
      .catch(() => setReceipts([]))
      .finally(() => setReceiptsLoading(false));
  }, [period, tab]);

  const filteredStores = stores.filter(s =>
    !search || s.name.toLowerCase().includes(search.toLowerCase())
  );
  const pinnedStores = filteredStores
    .filter(s => getPinnedRetailStoreRank(s) >= 0)
    .sort((a, b) => getPinnedRetailStoreRank(a) - getPinnedRetailStoreRank(b));
  const remainingStores = filteredStores.filter(s => getPinnedRetailStoreRank(s) < 0);
  const orderedFilteredStores = [...pinnedStores, ...remainingStores];

  const topStores = [...stores]
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, 15)
    .map(s => ({
      name: s.name.length > 22 ? s.name.slice(0, 22) + '…' : s.name,
      fullName: s.name,
      revenue: s.revenue,
      net: s.net,
    }));

  const selectedTinkoffRows = tinkoffDaily.filter(d => d.date?.startsWith(period));
  const selectedTinkoffStats = selectedTinkoffRows.reduce((acc, row) => {
    const revenue = row.revenue || 0;
    const commission = row.commission || 0;
    const refunds = row.refunds || 0;
    const net = row.net || 0;
    return {
      total_revenue: acc.total_revenue + revenue,
      total_commission: acc.total_commission + commission,
      total_refunds: acc.total_refunds + refunds,
      total_net: acc.total_net + net,
      best_day: revenue > (acc.best_day?.revenue || 0) ? { date: row.date, revenue } : acc.best_day,
    };
  }, {
    total_revenue: 0,
    total_commission: 0,
    total_refunds: 0,
    total_net: 0,
    best_day: null,
  });

  return (
    <div>
      <div className="kp-hero" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <Plate>Розница · магазины</Plate>
          <h1 style={{ margin: 0 }}>Розница</h1>
          <div style={{ color: 'var(--text2)', fontSize: 13, marginTop: 8 }}>
            Моя розница · {stores.length} собственных магазинов · Касса МойСклад + Эквайринг Т-Банк
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {periods.slice(0, 3).map(p => (
            <button key={p} onClick={() => setPeriod(p)} style={{
              padding: '6px 14px', borderRadius: 6, border: '1px solid var(--border)',
              background: period === p ? 'var(--accent)' : 'var(--bg2)',
              color: period === p ? '#fff' : 'var(--text2)', cursor: 'pointer', fontSize: 13,
            }}>{p}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 80, color: 'var(--text2)' }}>⏳ Загрузка данных магазинов...</div>
      ) : (
        <>
          {/* KPI Cards */}
          {(() => {
            const totalProj = getProjection(summary?.total_revenue, period);
            const cmpRevenue = totalProj ? totalProj.projection : summary?.total_revenue;
            const trendPct = prevSummary?.total_revenue
              ? ((cmpRevenue - prevSummary.total_revenue) / prevSummary.total_revenue * 100)
              : null;
            const trendPos = trendPct !== null && trendPct >= 0;
            const hasCashData = Object.keys(dailyStats).length > 0;
            const totalCashAll = Object.values(dailyStats).reduce((s, sd) => s + (sd.cash_balance || 0), 0);
            const [selectedYear, selectedMonth] = period.split('-').map(Number);
            const now = new Date();
            const isSelectedCurrentMonth = selectedYear === now.getFullYear() && selectedMonth === (now.getMonth() + 1);
            const todayStr = new Date().toISOString().slice(0, 10);
            const todayCash = Object.values(dailyStats).reduce((s, sd) => {
              const td = (sd.days || []).find(d => d.date === todayStr);
              return s + (td?.cash || 0);
            }, 0);
            const todayNoncash = Object.values(dailyStats).reduce((s, sd) => {
              const td = (sd.days || []).find(d => d.date === todayStr);
              return s + (td?.noncash || 0);
            }, 0);
            const periodCash = Object.values(dailyStats).reduce((s, sd) =>
              s + (sd.days || []).reduce((a, d) => a + (d.cash || 0), 0), 0);
            const periodNoncash = Object.values(dailyStats).reduce((s, sd) =>
              s + (sd.days || []).reduce((a, d) => a + (d.noncash || 0), 0), 0);
            const displayCash = isSelectedCurrentMonth ? todayCash : periodCash;
            const displayNoncash = isSelectedCurrentMonth ? todayNoncash : periodNoncash;
            const displayTotal = displayCash + displayNoncash;

            const kpiCard = (label, main, sub) => (
              <div style={{
                background: 'var(--bg2)', borderRadius: 12, padding: '18px 22px',
                border: '1px solid var(--border)', flex: 1, minWidth: 160,
                display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: 4,
              }}>
                <div style={{ fontSize: 12, color: 'var(--text2)' }}>{label}</div>
                <div style={{ flex: 1, display: 'flex', alignItems: 'center' }}>{main}</div>
                <div style={{ fontSize: 11, color: 'var(--text2)', minHeight: 16 }}>{sub || ''}</div>
              </div>
            );

            return (
              <div style={{ display: 'flex', gap: 14, marginBottom: 24, flexWrap: 'wrap', alignItems: 'stretch' }}>

                {/* Выручка касс */}
                {kpiCard(
                  '💰 Выручка касс (' + period + ')',
                  <span style={{ fontSize: 24, fontWeight: 700, color: '#0e9f6e' }}>{fmt(summary?.total_revenue)}</span>,
                  totalProj ? `+${fmt(totalProj.growth)} за 5 дн. · ${Math.round(totalProj.dailyAvg).toLocaleString('ru-RU')} ₽/день · ${totalProj.daysPassed}/${totalProj.daysInMonth} дн.` : null
                )}

                {/* Тренд */}
                {kpiCard(
                  '📈 Тренд vs пред. месяц',
                  trendPct !== null
                    ? <span style={{ fontSize: 24, fontWeight: 700, color: trendPos ? '#0e9f6e' : '#e04848' }}>{trendPos ? '↑' : '↓'} {Math.abs(trendPct).toFixed(1)}%</span>
                    : <span style={{ fontSize: 14, color: 'var(--text2)' }}>Нет данных</span>,
                  trendPct !== null
                    ? `${totalProj ? 'Прогноз' : 'Факт'} ${fmt(cmpRevenue)} vs ${fmt(prevSummary?.total_revenue)}`
                    : null
                )}

                {/* Суммарная касса */}
                {kpiCard(
                  '💵 Касса (все магазины)',
                  <span style={{ fontSize: 24, fontWeight: 700, color: hasCashData ? cashColor(totalCashAll) : 'var(--text2)' }}>
                    {hasCashData ? fmtCash(totalCashAll) : '…'}
                  </span>,
                  hasCashData ? `${Object.keys(dailyStats).length} магазинов` : 'загрузка...'
                )}

                {/* Возвраты */}
                {kpiCard(
                  '↩️ Возвраты',
                  <span style={{ fontSize: 24, fontWeight: 700, color: '#e04848' }}>{fmt(summary?.total_returns)}</span>,
                  null
                )}

                {/* Наличка сегодня */}
                {kpiCard(
                  isSelectedCurrentMonth ? '💵 Наличка сегодня' : `💵 Наличка (${period})`,
                  <span style={{ fontSize: 24, fontWeight: 700, color: '#0e9f6e' }}>
                    {hasCashData ? fmt(displayCash) : '…'}
                  </span>,
                  hasCashData ? `из ${fmt(displayTotal)} общей выручки` : 'загрузка...'
                )}

                {/* Безнал сегодня */}
                {kpiCard(
                  isSelectedCurrentMonth ? '💳 Безнал сегодня' : `💳 Безнал (${period})`,
                  <span style={{ fontSize: 24, fontWeight: 700, color: '#00a7dc' }}>
                    {hasCashData ? fmt(displayNoncash) : '…'}
                  </span>,
                  hasCashData && displayTotal > 0
                    ? `${Math.round(displayNoncash / displayTotal * 100)}% от выручки ${isSelectedCurrentMonth ? 'за день' : 'за месяц'}`
                    : 'загрузка...'
                )}

                {/* Чеки + Средний чек */}
                <div style={{
                  background: 'var(--bg2)', borderRadius: 12, padding: '18px 22px',
                  border: '1px solid var(--border)', flex: 1, minWidth: 200,
                  display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: 4,
                }}>
                  <div style={{ fontSize: 12, color: 'var(--text2)' }}>🧾 Продажи</div>
                  <div style={{ display: 'flex', gap: 20, alignItems: 'center', flex: 1 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 11, color: 'var(--text2)', marginBottom: 4 }}>Кол-во чеков</div>
                      <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--text1)', lineHeight: 1 }}>{(summary?.total_receipts || 0).toLocaleString('ru-RU')}</div>
                    </div>
                    <div style={{ width: 1, alignSelf: 'stretch', background: 'var(--border)', flexShrink: 0 }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 11, color: 'var(--text2)', marginBottom: 4 }}>Средний чек</div>
                      <div style={{ fontSize: 22, fontWeight: 700, color: '#00a7dc', lineHeight: 1 }}>{fmt(summary?.avg_check)}</div>
                    </div>
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text2)', minHeight: 16 }} />
                </div>

                {/* Наличка за месяц */}
                {kpiCard(
                  `💵 Наличка (${period})`,
                  <span style={{ fontSize: 24, fontWeight: 700, color: '#0e9f6e' }}>
                    {hasCashData
                      ? fmt(Object.values(dailyStats).reduce((s, sd) =>
                          s + (sd.days || []).filter(d => d.date?.startsWith(period)).reduce((a, d) => a + (d.cash || 0), 0), 0))
                      : '…'}
                  </span>,
                  hasCashData ? 'сумма по всем точкам' : 'загрузка...'
                )}

                {/* Эквайринг */}
                {(selectedTinkoffRows.length > 0 || tinkoffStats) && kpiCard(
                  `💳 Эквайринг Т-Банк (${period})`,
                  <span style={{ fontSize: 24, fontWeight: 700, color: '#e08a00' }}>{fmt(selectedTinkoffStats.total_net)}</span>,
                  `Комиссия: ${fmt(selectedTinkoffStats.total_commission)}`
                )}
              </div>
            );
          })()}

          {/* Tabs */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
            <Tab label="📊 Магазины" active={tab === 'stores'} onClick={() => setTab('stores')} />
            <Tab label="🏆 Рейтинг" active={tab === 'ranking'} onClick={() => setTab('ranking')} />
            <Tab label="💳 Эквайринг" active={tab === 'acquiring'} onClick={() => setTab('acquiring')} />
            <Tab label="📤 Расходы" active={tab === 'expenses'} onClick={() => setTab('expenses')} />
            <Tab label="💚 Доходы" active={tab === 'receipts'} onClick={() => setTab('receipts')} />
          </div>

          {/* STORES TAB */}
          {tab === 'stores' && (
            <>
              <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center' }}>
                <input
                  value={search} onChange={e => setSearch(e.target.value)}
                  placeholder="🔍 Поиск по названию магазина..."
                  style={{ flex: 1, maxWidth: 360, padding: '8px 14px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--text1)', fontSize: 13 }}
                />
                <div style={{ color: 'var(--text2)', fontSize: 13 }}>Показано: {filteredStores.length} из {stores.length}</div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(300px, 1fr))', gap: 12 }}>
                {orderedFilteredStores.map((s, idx) => {
                  const cardPeriod = storeCardPeriods[s.store_external_id] || period;
                  const monthStore = storeHistory[cardPeriod]?.[s.store_external_id];
                  const displayStore = monthStore || (cardPeriod === period ? s : null);
                  const revenue = displayStore?.revenue || 0;
                  const avgCheck = displayStore?.avg_check || 0;
                  const returnsAmount = displayStore?.returns_amount || 0;
                  const prevCardPeriod = getPrevPeriod(periods, cardPeriod);
                  const prev = prevCardPeriod ? storeHistory[prevCardPeriod]?.[s.store_external_id] : prevStoreMap[s.store_external_id];
                  const prevRevenue = prev?.revenue || 0;
                  const periodTotal = Object.values(storeHistory[cardPeriod] || {}).reduce((sum, item) => sum + (item.revenue || 0), 0);
                  const shareOfTotal = periodTotal ? (revenue / periodTotal * 100) : 0;
                  const color = COLORS[idx % COLORS.length];
                  const proj = getProjection(revenue, cardPeriod);
                  const trendValue = proj ? proj.projection : revenue;
                  const trendDelta = prevRevenue > 0 ? ((trendValue - prevRevenue) / prevRevenue * 100) : null;
                  return (
                    <div key={s.store_external_id} style={{
                      background: 'var(--bg2)', borderRadius: 12, padding: '16px 20px',
                      border: `1px solid var(--border)`, borderLeft: `4px solid ${color}`,
                      transition: 'transform 0.1s', cursor: 'default',
                    }}
                      onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'}
                      onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
                    >
                      <div style={{ fontWeight: 700, fontSize: 14, color, marginBottom: 10, lineHeight: 1.3 }}>
                        {s.name}
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                        <div>
                          <div style={{ fontSize: 11, color: 'var(--text2)' }}>Выручка</div>
                          <div style={{ fontSize: 18, fontWeight: 700, color: '#0e9f6e' }}>{fmt(revenue)}</div>
                        </div>
                        <div>
                          <MonthArrows
                            period={cardPeriod}
                            periods={periods}
                            onChange={nextPeriod => setStoreCardPeriods(prev => ({ ...prev, [s.store_external_id]: nextPeriod }))}
                          />
                          <div style={{ fontSize: 18, fontWeight: 700, color: prevRevenue > 0 ? 'var(--text1)' : 'var(--text2)' }}>
                            {prevRevenue > 0 ? fmt(prevRevenue) : '—'}
                          </div>
                        </div>
                        <div>
                          <div style={{ fontSize: 11, color: 'var(--text2)' }}>Средний чек</div>
                          <div style={{ fontSize: 15, fontWeight: 600, color: '#00a7dc' }}>{fmt(avgCheck)}</div>
                        </div>
                        <div>
                          <div style={{ fontSize: 11, color: 'var(--text2)' }}>Возвраты</div>
                          <div style={{ fontSize: 15, fontWeight: 600, color: returnsAmount > 0 ? '#e04848' : 'var(--text2)' }}>{fmt(returnsAmount)}</div>
                        </div>
                      </div>

                      {/* Навигатор по дням: касса, выручка нал/безнал */}
                      {(() => {
                        const sid = s.store_external_id;
                        const sd = dailyStats[sid];
                        const cashBalance = sd?.cash_balance ?? null;
                        const days = sd?.days || [];
                        const today = new Date().toISOString().slice(0, 10);
                        const idx = storeDayIdx[sid] ?? (days.length - 1);
                        if (!days.length && cashBalance === null) return null;
                        const day = days[idx] || { date: today, revenue: 0, cash: 0, noncash: 0, count: 0 };
                        const isToday = day.date === today;
                        const dayLabel = day.date ? new Date(day.date + 'T12:00:00').toLocaleDateString('ru-RU', { weekday: 'short', day: 'numeric', month: 'short' }) : '';
                        const canPrev = idx > 0;
                        const canNext = idx < days.length - 1;
                        const setIdx = (v) => setStoreDayIdx(prev => ({ ...prev, [sid]: v }));

                        return (
                          <div style={{
                            marginTop: 10, marginBottom: 10, padding: '10px 12px', borderRadius: 8,
                            background: 'rgba(0,167,220,0.06)', border: '1px solid rgba(0,167,220,0.2)',
                          }}>
                            {cashBalance !== null && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                                <span style={{ fontSize: 10, color: cashColor(cashBalance), textTransform: 'uppercase', fontWeight: 600 }}>💰 В кассе</span>
                                <span style={{ fontSize: 16, fontWeight: 800, color: cashColor(cashBalance) }}>{fmtCash(cashBalance)}</span>
                              </div>
                            )}

                            {days.length > 0 && (
                              <>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 8 }}>
                                  <button onClick={() => canPrev && setIdx(idx - 1)} disabled={!canPrev} style={{
                                    background: canPrev ? 'rgba(0,167,220,0.15)' : 'transparent',
                                    color: canPrev ? '#00a7dc' : 'var(--text2)',
                                    border: '1px solid var(--border)', borderRadius: 8,
                                    width: 28, height: 28, fontSize: 14, cursor: canPrev ? 'pointer' : 'default',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
                                  }}>◀</button>
                                  <div style={{ textAlign: 'center', minWidth: 100 }}>
                                    <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text1)' }}>{dayLabel}</div>
                                    <div style={{ fontSize: 10, color: isToday ? '#0e9f6e' : 'var(--text2)', fontWeight: 600 }}>
                                      {isToday ? '● сегодня' : day.date}
                                    </div>
                                  </div>
                                  <button onClick={() => canNext && setIdx(idx + 1)} disabled={!canNext} style={{
                                    background: canNext ? 'rgba(0,167,220,0.15)' : 'transparent',
                                    color: canNext ? '#00a7dc' : 'var(--text2)',
                                    border: '1px solid var(--border)', borderRadius: 8,
                                    width: 28, height: 28, fontSize: 14, cursor: canNext ? 'pointer' : 'default',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
                                  }}>▶</button>
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6 }}>
                                  <div style={{ background: 'rgba(14,159,110,0.1)', borderRadius: 6, padding: '6px 8px', textAlign: 'center' }}>
                                    <div style={{ fontSize: 9, color: 'var(--text2)', textTransform: 'uppercase' }}>Выручка</div>
                                    <div style={{ fontSize: 15, fontWeight: 800, color: '#0e9f6e' }}>{fmt(day.revenue)}</div>
                                  </div>
                                  <div style={{ background: 'rgba(14,159,110,0.08)', borderRadius: 6, padding: '6px 8px', textAlign: 'center' }}>
                                    <div style={{ fontSize: 9, color: 'var(--text2)', textTransform: 'uppercase' }}>💵 Нал</div>
                                    <div style={{ fontSize: 14, fontWeight: 700, color: '#0e9f6e' }}>{fmt(day.cash)}</div>
                                  </div>
                                  <div style={{ background: 'rgba(0,167,220,0.08)', borderRadius: 6, padding: '6px 8px', textAlign: 'center' }}>
                                    <div style={{ fontSize: 9, color: 'var(--text2)', textTransform: 'uppercase' }}>💳 Безнал</div>
                                    <div style={{ fontSize: 14, fontWeight: 700, color: '#00a7dc' }}>{fmt(day.noncash)}</div>
                                  </div>
                                </div>

                                <div style={{ textAlign: 'center', marginTop: 6, fontSize: 11, color: 'var(--text2)' }}>
                                  🧾 {day.count} {day.count === 1 ? 'чек' : day.count < 5 ? 'чека' : 'чеков'}
                                  {day.count > 0 && <span style={{ marginLeft: 8 }}>· ср. чек {fmt(Math.round(day.revenue / day.count))}</span>}
                                </div>
                              </>
                            )}
                          </div>
                        );
                      })()}

                      {/* Тренд vs прошлый месяц */}
                      {prevRevenue > 0 && trendDelta !== null && (
                        <div style={{
                          marginTop: 10, padding: '8px 12px', borderRadius: 8,
                          background: trendDelta >= 0 ? 'rgba(14,159,110,0.08)' : 'rgba(224,72,72,0.08)',
                          border: `1px solid ${trendDelta >= 0 ? 'rgba(14,159,110,0.25)' : 'rgba(224,72,72,0.25)'}`,
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: 11, color: 'var(--text2)' }}>{proj ? '📈 Тренд vs пред. месяц' : 'vs пред. месяц'}</span>
                            <span style={{ fontSize: 14, fontWeight: 700, color: trendDelta >= 0 ? '#0e9f6e' : '#e04848' }}>
                              {trendDelta >= 0 ? '↑' : '↓'} {Math.abs(trendDelta).toFixed(1)}%
                            </span>
                          </div>
                          {proj && (
                            <div style={{ fontSize: 10, color: 'var(--text2)', marginTop: 3 }}>
                              Прогноз {fmt(proj.projection)} vs {fmt(prevRevenue)}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Прогноз на конец месяца */}
                      {proj && (
                        <div style={{
                          marginTop: 10, padding: '8px 10px', borderRadius: 8,
                          background: 'rgba(0,167,220,0.08)', border: '1px solid rgba(0,167,220,0.2)',
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                            <span style={{ fontSize: 11, color: 'var(--text2)' }}>📈 Прогноз на месяц</span>
                            <span style={{ fontSize: 13, fontWeight: 700, color: '#00a7dc' }}>{fmt(proj.projection)}</span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--text2)', marginBottom: 5 }}>
                            <span>+{fmt(proj.growth)} за {proj.daysLeft} дн.</span>
                            <span>{fmt(Math.round(proj.dailyAvg))}/день · {proj.daysPassed}/{proj.daysInMonth} дн.</span>
                          </div>
                          <div style={{ height: 3, background: 'var(--bg3)', borderRadius: 2, overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${(proj.daysPassed / proj.daysInMonth) * 100}%`, background: '#00a7dc', borderRadius: 2 }} />
                          </div>
                        </div>
                      )}

                      <div style={{ marginTop: 8 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text2)', marginBottom: 4 }}>
                          <span>Доля в общей выручке</span>
                          <span>{shareOfTotal.toFixed(1)}%</span>
                        </div>
                        <div style={{ height: 4, background: 'var(--bg3)', borderRadius: 2, overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: `${Math.min(shareOfTotal, 100)}%`, background: color, borderRadius: 2 }} />
                        </div>
                      </div>

                      {/* Расходы — раскрывающийся блок */}
                      {(() => {
                        const sid = s.store_external_id;
                        const storeExp = expenses[sid];
                        const allItems = storeExp?.items || [];
                        const dailyTotals = storeExp?.daily_totals || {};
                        const totalExpAll = allItems.reduce((a, b) => a + (b.amount || 0), 0);
                        const isOpen = expandedExpenses[sid];
                        const today = new Date().toISOString().slice(0, 10);
                        const filter = expFilter[sid] || { type: 'today' };
                        const setFilter = (f) => setExpFilter(prev => ({ ...prev, [sid]: f }));

                        let filtered = allItems;
                        let filterLabel = 'Сегодня';
                        if (filter.type === 'today') {
                          filtered = allItems.filter(i => i.date === today);
                          filterLabel = 'Сегодня';
                        } else if (filter.type === 'date' && filter.date) {
                          filtered = allItems.filter(i => i.date === filter.date);
                          filterLabel = new Date(filter.date + 'T12:00:00').toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
                        } else if (filter.type === 'month' && filter.month) {
                          filtered = allItems.filter(i => i.date?.startsWith(filter.month));
                          filterLabel = filter.month;
                        }
                        const filteredTotal = filtered.reduce((a, b) => a + (b.amount || 0), 0);

                        // Always include last 4 months so user can always pick a month
                        const nowD = new Date();
                        const staticMonths = Array.from({ length: 4 }, (_, i) => {
                          const d = new Date(nowD.getFullYear(), nowD.getMonth() - i, 1);
                          return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
                        });
                        const months = [...new Set([...staticMonths, ...allItems.map(i => i.date?.slice(0, 7)).filter(Boolean)])].sort().reverse();

                        return (
                          <div style={{ marginTop: 10, borderTop: '1px solid var(--border)', paddingTop: 8 }}>
                            <button onClick={() => setExpandedExpenses(prev => ({ ...prev, [sid]: !prev[sid] }))}
                              style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%',
                                background: 'none', border: 'none', cursor: 'pointer', padding: '4px 0', color: 'var(--text2)',
                              }}>
                              <span style={{ fontSize: 12, fontWeight: 600 }}>📤 Расходы (выдачи из кассы)</span>
                              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                {totalExpAll > 0 && <span style={{ fontSize: 11, color: '#e04848', fontWeight: 600 }}>{fmt(totalExpAll)}</span>}
                                {totalExpAll === 0 && <span style={{ fontSize: 11, color: 'var(--text2)' }}>нет данных</span>}
                                <span style={{
                                  display: 'inline-block', transition: 'transform 0.2s',
                                  transform: isOpen ? 'rotate(180deg)' : 'rotate(0)', fontSize: 14,
                                }}>▾</span>
                              </span>
                            </button>

                            {isOpen && (
                              <div style={{ marginTop: 8 }}>
                                {/* Фильтры */}
                                <div style={{ display: 'flex', gap: 4, marginBottom: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                                  <button onClick={() => setFilter({ type: 'today' })} style={{
                                    padding: '4px 10px', borderRadius: 6, border: '1px solid var(--border)', fontSize: 11,
                                    background: filter.type === 'today' ? '#e0484820' : 'var(--bg3)',
                                    color: filter.type === 'today' ? '#e04848' : 'var(--text2)',
                                    cursor: 'pointer', fontWeight: filter.type === 'today' ? 600 : 400,
                                  }}>Сегодня</button>

                                  <input type="date" value={filter.type === 'date' ? (filter.date || '') : ''}
                                    onChange={e => setFilter({ type: 'date', date: e.target.value })}
                                    style={{
                                      padding: '3px 8px', borderRadius: 6, border: '1px solid var(--border)',
                                      fontSize: 11, background: filter.type === 'date' ? '#e0484810' : 'var(--bg3)',
                                      color: 'var(--text1)', cursor: 'pointer', maxWidth: 130,
                                    }} />

                                  {months.map(m => {
                                    const label = new Date(m + '-02').toLocaleDateString('ru-RU', { month: 'short', year: '2-digit' });
                                    const active = filter.type === 'month' && filter.month === m;
                                    return (
                                      <button key={m} onClick={() => setFilter({ type: 'month', month: m })} style={{
                                        padding: '4px 10px', borderRadius: 6, border: '1px solid var(--border)', fontSize: 11,
                                        background: active ? '#e0484820' : 'var(--bg3)',
                                        color: active ? '#e04848' : 'var(--text2)',
                                        cursor: 'pointer', fontWeight: active ? 600 : 400,
                                      }}>{label}</button>
                                    );
                                  })}
                                </div>

                                {/* Итого по фильтру */}
                                <div style={{
                                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                  padding: '6px 10px', borderRadius: 6, marginBottom: 6,
                                  background: 'rgba(224,72,72,0.06)', border: '1px solid rgba(224,72,72,0.15)',
                                }}>
                                  <span style={{ fontSize: 11, color: 'var(--text2)' }}>Итого ({filterLabel})</span>
                                  <span style={{ fontSize: 14, fontWeight: 700, color: '#e04848' }}>{fmt(filteredTotal)}</span>
                                </div>

                                {/* Список расходов */}
                                <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                                  {filtered.length === 0 && (
                                    <div style={{ textAlign: 'center', padding: 12, color: 'var(--text2)', fontSize: 12 }}>
                                      Нет выдач за выбранный период
                                    </div>
                                  )}
                                  {filtered.map((item, i) => (
                                    <div key={i} style={{
                                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                      padding: '5px 0', borderBottom: '1px solid var(--border)', fontSize: 12,
                                    }}>
                                      <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontWeight: 500, color: 'var(--text1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                          {item.description || 'Выдача'}
                                        </div>
                                        <div style={{ fontSize: 10, color: 'var(--text2)' }}>
                                          {item.date && new Date(item.date + 'T12:00:00').toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' })}
                                        </div>
                                      </div>
                                      <div style={{ fontWeight: 700, color: '#e04848', whiteSpace: 'nowrap', marginLeft: 8 }}>
                                        −{fmt(item.amount)}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  );
                })}
              </div>

            </>
          )}

          {/* RANKING TAB */}
          {tab === 'ranking' && (
            <>
              <div style={{ background: 'var(--bg2)', borderRadius: 12, padding: '20px 24px', border: '1px solid var(--border)', marginBottom: 20 }}>
                <div style={{ fontWeight: 600, marginBottom: 16 }}>🏆 Топ магазинов по выручке</div>
                <ResponsiveContainer width="100%" height={Math.max(300, topStores.length * 40)}>
                  <BarChart data={topStores} layout="vertical" margin={{ top: 4, right: 60, left: 0, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" horizontal={false} />
                    <XAxis type="number" tickFormatter={fmtShort} tick={{ fontSize: 11, fill: 'var(--text2)' }} />
                    <YAxis type="category" dataKey="name" width={180} tick={{ fontSize: 11, fill: 'var(--text1)' }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="revenue" name="Выручка" radius={[0, 4, 4, 0]}>
                      {topStores.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div style={{ background: 'var(--bg2)', borderRadius: 12, padding: '20px 24px', border: '1px solid var(--border)' }}>
                <div style={{ fontWeight: 600, marginBottom: 16 }}>📋 Полный рейтинг магазинов</div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr>
                      {['#', 'Магазин', 'Выручка', 'Пред. мес.', 'Δ%', 'Чеков', 'Ср. чек', 'Возвраты', 'Доля'].map(h => (
                        <th key={h} style={{ padding: '8px 12px', textAlign: h === 'Магазин' || h === '#' ? 'left' : 'right', color: 'var(--text2)', fontWeight: 600, borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap', fontSize: 12 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {stores.map((s, idx) => {
                      const share = summary?.total_revenue ? (s.revenue / summary.total_revenue * 100) : 0;
                      const prevS = prevStoreMap[s.store_external_id];
                      const prevRev = prevS?.revenue || 0;
                      const d = prevRev > 0 ? ((s.revenue - prevRev) / prevRev * 100) : null;
                      return (
                        <tr key={s.store_external_id} style={{ borderBottom: '1px solid var(--border)' }}>
                          <td style={{ padding: '8px 12px', color: 'var(--text2)', fontWeight: 600 }}>{idx + 1}</td>
                          <td style={{ padding: '8px 12px', color: COLORS[idx % COLORS.length], fontWeight: 500 }}>{s.name}</td>
                          <td style={{ padding: '8px 12px', textAlign: 'right', color: '#0e9f6e', fontWeight: 600 }}>{fmt(s.revenue)}</td>
                          <td style={{ padding: '8px 12px', textAlign: 'right', color: 'var(--text2)' }}>{prevRev > 0 ? fmt(prevRev) : '—'}</td>
                          <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, color: d === null ? 'var(--text2)' : d >= 0 ? '#0e9f6e' : '#e04848' }}>
                            {d !== null ? `${d >= 0 ? '↑' : '↓'}${Math.abs(d).toFixed(1)}%` : '—'}
                          </td>
                          <td style={{ padding: '8px 12px', textAlign: 'right' }}>{s.receipts_count}</td>
                          <td style={{ padding: '8px 12px', textAlign: 'right', color: '#00a7dc' }}>{fmt(s.avg_check)}</td>
                          <td style={{ padding: '8px 12px', textAlign: 'right', color: s.returns_amount > 0 ? '#e04848' : 'var(--text2)' }}>{fmt(s.returns_amount)}</td>
                          <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                            <span style={{ color: COLORS[idx % COLORS.length], fontWeight: 600 }}>{share.toFixed(1)}%</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr style={{ borderTop: '2px solid var(--border)', fontWeight: 700 }}>
                      <td colSpan={2} style={{ padding: '10px 12px', color: 'var(--text1)' }}>Итого</td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', color: '#0e9f6e' }}>{fmt(summary?.total_revenue)}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', color: 'var(--text2)' }}>{prevSummary?.total_revenue > 0 ? fmt(prevSummary.total_revenue) : '—'}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 700, color: prevSummary?.total_revenue > 0 ? ((summary?.total_revenue - prevSummary.total_revenue) / prevSummary.total_revenue * 100 >= 0 ? '#0e9f6e' : '#e04848') : 'var(--text2)' }}>
                        {prevSummary?.total_revenue > 0
                          ? `${(summary?.total_revenue - prevSummary.total_revenue) / prevSummary.total_revenue * 100 >= 0 ? '↑' : '↓'}${Math.abs((summary?.total_revenue - prevSummary.total_revenue) / prevSummary.total_revenue * 100).toFixed(1)}%`
                          : '—'}
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'right' }}>{(summary?.total_receipts || 0).toLocaleString('ru-RU')}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', color: '#00a7dc' }}>{fmt(summary?.avg_check)}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', color: '#e04848' }}>{fmt(summary?.total_returns)}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'right' }}>100%</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </>
          )}

          {/* EXPENSES TAB */}
          {tab === 'expenses' && (() => {
            const today = new Date().toISOString().slice(0, 10);
            const weekAgo = new Date(Date.now() - 7 * 24 * 3600 * 1000).toISOString().slice(0, 10);
            const expenseStoreOptions = [
              { value: 'all', label: 'Все филиалы' },
              ...stores
                .map(s => ({ value: s.store_external_id, label: s.name }))
                .sort((a, b) => a.label.localeCompare(b.label, 'ru')),
            ];

            // Aggregate all items from all stores
            const allExpItems = [];
            Object.entries(expenses).forEach(([sid, storeExp]) => {
              const storeName = stores.find(s => s.store_external_id === sid)?.name || storeExp.store_name || sid;
              (storeExp.items || []).forEach(item => {
                allExpItems.push({ ...item, store_name: storeName, store_id: sid });
              });
            });
            allExpItems.sort((a, b) => (b.moment || b.date || '').localeCompare(a.moment || a.date || ''));

            let filtered = allExpItems;
            let filterLabel = period;
            if (expTabFilter === 'today') {
              filtered = allExpItems.filter(i => i.date === today);
              filterLabel = 'сегодня';
            } else if (expTabFilter === 'week') {
              filtered = allExpItems.filter(i => i.date >= weekAgo);
              filterLabel = 'неделя';
            } else if (expTabFilter === 'month') {
              filtered = allExpItems.filter(i => i.date?.startsWith(period));
              filterLabel = period;
            }
            if (expenseStoreFilter !== 'all') {
              filtered = filtered.filter(i => i.store_id === expenseStoreFilter);
            }

            const totalFiltered = filtered.reduce((s, i) => s + (i.amount || 0), 0);

            // Summary by store
            const byStore = {};
            filtered.forEach(item => {
              if (!byStore[item.store_id]) byStore[item.store_id] = { name: item.store_name, total: 0, count: 0 };
              byStore[item.store_id].total += item.amount || 0;
              byStore[item.store_id].count++;
            });
            const storeChart = Object.values(byStore).sort((a, b) => b.total - a.total);
            const storeColorMap = {};
            storeChart.forEach((s, i) => { storeColorMap[s.name] = COLORS[i % COLORS.length]; });

            const isLoading = Object.keys(expenses).length === 0;

            return (
              <div>
                {/* Period filter */}
                <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
                  {[
                    { key: 'today', label: 'Сегодня' },
                    { key: 'week', label: 'Неделя' },
                    { key: 'month', label: `Месяц (${period})` },
                  ].map(f => (
                    <Tab key={f.key} label={f.label} active={expTabFilter === f.key} onClick={() => setExpTabFilter(f.key)} />
                  ))}
                  <select
                    value={expenseStoreFilter}
                    onChange={e => setExpenseStoreFilter(e.target.value)}
                    style={{
                      background: 'var(--bg2)', color: 'var(--text1)',
                      border: '1px solid var(--border)', borderRadius: 8,
                      padding: '8px 12px', fontSize: 13, outline: 'none', minWidth: 220,
                    }}
                  >
                    {expenseStoreOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  {isLoading && <span style={{ fontSize: 12, color: 'var(--text2)', marginLeft: 8 }}>⏳ Загрузка данных...</span>}
                </div>

                {/* Summary cards */}
                <div style={{ display: 'flex', gap: 14, marginBottom: 20, flexWrap: 'wrap' }}>
                  <div style={{ background: 'var(--bg2)', borderRadius: 12, padding: '18px 22px', border: '1px solid var(--border)', flex: 1, minWidth: 160 }}>
                    <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 6 }}>📤 Итого выдач ({filterLabel})</div>
                    <div style={{ fontSize: 26, fontWeight: 700, color: '#e04848' }}>−{fmt(totalFiltered)}</div>
                  </div>
                  <div style={{ background: 'var(--bg2)', borderRadius: 12, padding: '18px 22px', border: '1px solid var(--border)', flex: 1, minWidth: 160 }}>
                    <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 6 }}>🔢 Операций</div>
                    <div style={{ fontSize: 26, fontWeight: 700, color: 'var(--text1)' }}>{filtered.length}</div>
                  </div>
                  <div style={{ background: 'var(--bg2)', borderRadius: 12, padding: '18px 22px', border: '1px solid var(--border)', flex: 1, minWidth: 160 }}>
                    <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 6 }}>📊 Средняя выдача</div>
                    <div style={{ fontSize: 26, fontWeight: 700, color: '#e08a00' }}>{fmt(filtered.length ? Math.round(totalFiltered / filtered.length) : 0)}</div>
                  </div>
                  <div style={{ background: 'var(--bg2)', borderRadius: 12, padding: '18px 22px', border: '1px solid var(--border)', flex: 1, minWidth: 160 }}>
                    <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 6 }}>🏪 Точек с расходами</div>
                    <div style={{ fontSize: 26, fontWeight: 700, color: '#00a7dc' }}>{storeChart.length}</div>
                  </div>
                </div>

                {/* Bar chart by store */}
                {storeChart.length > 0 && (
                  <div style={{ background: 'var(--bg2)', borderRadius: 12, padding: '20px 24px', border: '1px solid var(--border)', marginBottom: 20 }}>
                    <div style={{ fontWeight: 600, marginBottom: 16 }}>📊 Выдачи по точкам</div>
                    <ResponsiveContainer width="100%" height={Math.max(180, storeChart.length * 40)}>
                      <BarChart data={storeChart} layout="vertical" margin={{ top: 4, right: 100, left: 0, bottom: 4 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" horizontal={false} />
                        <XAxis type="number" tickFormatter={fmtShort} tick={{ fontSize: 11, fill: 'var(--text2)' }} />
                        <YAxis type="category" dataKey="name" width={200} tick={{ fontSize: 11, fill: 'var(--text1)' }} />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="total" name="Выдачи" radius={[0, 4, 4, 0]}>
                          {storeChart.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {/* Full list */}
                <div style={{ background: 'var(--bg2)', borderRadius: 12, border: '1px solid var(--border)', overflow: 'hidden' }}>
                  <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', fontWeight: 600, fontSize: 14 }}>
                    📋 Все выдачи · {filtered.length} операций
                  </div>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead>
                        <tr>
                          {['Дата', 'Точка', 'Описание', 'Сумма'].map(h => (
                            <th key={h} style={{
                              padding: '10px 16px', textAlign: h === 'Сумма' ? 'right' : 'left',
                              color: 'var(--text2)', fontWeight: 600, borderBottom: '1px solid var(--border)',
                              fontSize: 12, whiteSpace: 'nowrap', background: 'var(--bg2)',
                            }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {isLoading && (
                          <tr><td colSpan={4} style={{ padding: 32, textAlign: 'center', color: 'var(--text2)' }}>
                            ⏳ Загружаем данные о выдачах из кассы...
                          </td></tr>
                        )}
                        {!isLoading && filtered.length === 0 && (
                          <tr><td colSpan={4} style={{ padding: 32, textAlign: 'center', color: 'var(--text2)' }}>
                            Нет выдач за выбранный период
                          </td></tr>
                        )}
                        {filtered.map((item, i) => (
                          <tr key={i} style={{
                            borderBottom: '1px solid var(--border)',
                            background: i % 2 === 0 ? 'transparent' : 'rgba(30,36,37,0.01)',
                          }}>
                            <td style={{ padding: '9px 16px', color: 'var(--text2)', whiteSpace: 'nowrap' }}>
                              {item.date && new Date(item.date + 'T12:00:00').toLocaleDateString('ru-RU', { day: 'numeric', month: 'short', year: '2-digit' })}
                            </td>
                            <td style={{ padding: '9px 16px', color: storeColorMap[item.store_name] || 'var(--text1)', fontWeight: 500, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {item.store_name}
                            </td>
                            <td style={{ padding: '9px 16px', color: 'var(--text1)', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {item.description || 'Выдача из кассы'}
                            </td>
                            <td style={{ padding: '9px 16px', textAlign: 'right', color: '#e04848', fontWeight: 700, whiteSpace: 'nowrap' }}>
                              −{fmt(item.amount)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      {filtered.length > 0 && (
                        <tfoot>
                          <tr style={{ borderTop: '2px solid var(--border)', background: 'rgba(224,72,72,0.04)' }}>
                            <td colSpan={3} style={{ padding: '12px 16px', fontWeight: 700, color: 'var(--text1)', fontSize: 14 }}>Итого</td>
                            <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 800, color: '#e04848', fontSize: 16 }}>−{fmt(totalFiltered)}</td>
                          </tr>
                        </tfoot>
                      )}
                    </table>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* RECEIPTS TAB */}
          {tab === 'receipts' && (() => {
            const receiptStoreOptions = [
              { value: 'all', label: 'Все филиалы' },
              ...stores
                .map(s => ({ value: s.store_external_id, label: s.name }))
                .sort((a, b) => a.label.localeCompare(b.label, 'ru')),
            ];
            const filteredReceipts = receiptStoreFilter === 'all'
              ? receipts
              : receipts.filter(r => r.store_id === receiptStoreFilter);
            const totalReceiptSum = filteredReceipts.reduce((s, r) => s + (r.sum || 0), 0);
            const avgReceipt = filteredReceipts.length ? Math.round(totalReceiptSum / filteredReceipts.length) : 0;

            const paymentBadge = (row) => {
              if (row.payment_type === 'Нал') {
                return <span style={{ color: '#0e9f6e', fontWeight: 700 }}>Нал</span>;
              }
              if (row.payment_type === 'БН') {
                return <span style={{ color: '#00a7dc', fontWeight: 700 }}>БН</span>;
              }
              return <span style={{ color: '#e08a00', fontWeight: 700 }}>Смешанная</span>;
            };

            return (
              <div>
                <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
                  <select
                    value={receiptStoreFilter}
                    onChange={e => setReceiptStoreFilter(e.target.value)}
                    style={{
                      background: 'var(--bg2)', color: 'var(--text1)',
                      border: '1px solid var(--border)', borderRadius: 8,
                      padding: '8px 12px', fontSize: 13, outline: 'none', minWidth: 220,
                    }}
                  >
                    {receiptStoreOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                <div style={{ display: 'flex', gap: 14, marginBottom: 20, flexWrap: 'wrap' }}>
                  <div style={{ background: 'var(--bg2)', borderRadius: 12, padding: '18px 22px', border: '1px solid var(--border)', flex: 1, minWidth: 180 }}>
                    <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 6 }}>🧾 Доходов за {period}</div>
                    <div style={{ fontSize: 26, fontWeight: 700, color: 'var(--text1)' }}>{filteredReceipts.length.toLocaleString('ru-RU')}</div>
                  </div>
                  <div style={{ background: 'var(--bg2)', borderRadius: 12, padding: '18px 22px', border: '1px solid var(--border)', flex: 1, minWidth: 180 }}>
                    <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 6 }}>💰 Сумма доходов</div>
                    <div style={{ fontSize: 26, fontWeight: 700, color: '#0e9f6e' }}>{fmt(totalReceiptSum)}</div>
                  </div>
                  <div style={{ background: 'var(--bg2)', borderRadius: 12, padding: '18px 22px', border: '1px solid var(--border)', flex: 1, minWidth: 180 }}>
                    <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 6 }}>📈 Средний чек</div>
                    <div style={{ fontSize: 26, fontWeight: 700, color: '#00a7dc' }}>{fmt(avgReceipt)}</div>
                  </div>
                </div>

                <div style={{ background: 'var(--bg2)', borderRadius: 12, border: '1px solid var(--border)', overflow: 'hidden' }}>
                  <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', fontWeight: 600, fontSize: 14 }}>
                    💚 Список доходов · {period}
                  </div>
                  <div style={{ maxHeight: 620, overflow: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead>
                        <tr>
                          {['Дата', 'Время', 'Точка', 'Чек', 'Сумма', 'Оплата'].map(h => (
                            <th key={h} style={{
                              padding: '10px 16px',
                              textAlign: h === 'Сумма' ? 'right' : 'left',
                              color: 'var(--text2)',
                              fontWeight: 600,
                              borderBottom: '1px solid var(--border)',
                              fontSize: 12,
                              whiteSpace: 'nowrap',
                              background: 'var(--bg2)',
                              position: 'sticky',
                              top: 0,
                              zIndex: 1,
                            }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {receiptsLoading && (
                          <tr>
                            <td colSpan={6} style={{ padding: 32, textAlign: 'center', color: 'var(--text2)' }}>
                              ⏳ Загружаем список чеков...
                            </td>
                          </tr>
                        )}
                        {!receiptsLoading && filteredReceipts.length === 0 && (
                          <tr>
                            <td colSpan={6} style={{ padding: 32, textAlign: 'center', color: 'var(--text2)' }}>
                              Нет доходов за выбранный период
                            </td>
                          </tr>
                        )}
                        {filteredReceipts.map((row, i) => (
                          <tr key={row.id || i} style={{ borderBottom: '1px solid var(--border)', background: i % 2 === 0 ? 'transparent' : 'rgba(30,36,37,0.01)' }}>
                            <td style={{ padding: '9px 16px', color: 'var(--text2)', whiteSpace: 'nowrap' }}>
                              {row.date ? new Date(row.date + 'T12:00:00').toLocaleDateString('ru-RU', { day: '2-digit', month: 'short' }) : '—'}
                            </td>
                            <td style={{ padding: '9px 16px', color: 'var(--text1)', whiteSpace: 'nowrap', fontWeight: 600 }}>
                              {row.time || '—'}
                            </td>
                            <td style={{ padding: '9px 16px', color: 'var(--text1)', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {row.store_name}
                            </td>
                            <td style={{ padding: '9px 16px', color: 'var(--text2)', whiteSpace: 'nowrap' }}>
                              {row.receipt_no || '—'}
                            </td>
                            <td style={{ padding: '9px 16px', textAlign: 'right', color: '#0e9f6e', fontWeight: 700, whiteSpace: 'nowrap' }}>
                              {fmt(row.sum)}
                            </td>
                            <td style={{ padding: '9px 16px', whiteSpace: 'nowrap' }}>
                              <div>{paymentBadge(row)}</div>
                              {row.payment_type === 'Смешанная' && (
                                <div style={{ fontSize: 10, color: 'var(--text2)', marginTop: 2 }}>
                                  Нал {fmt(row.cash)} · БН {fmt(row.noncash)}
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* ACQUIRING TAB */}
          {tab === 'acquiring' && (
            <>
              {(selectedTinkoffRows.length > 0 || tinkoffStats) && (
                <div style={{ display: 'flex', gap: 14, marginBottom: 20, flexWrap: 'wrap' }}>
                  <Card icon="💰" label={`Зачисления (${period})`} value={fmt(selectedTinkoffStats.total_revenue)} color="#0e9f6e" />
                  <Card icon="✅" label="Чистая (без комиссии)" value={fmt(selectedTinkoffStats.total_net)} color="#00a7dc" />
                  <Card icon="🏦" label="Комиссия банка" value={fmt(selectedTinkoffStats.total_commission)} color="#e08a00" sub={`${selectedTinkoffStats.total_revenue ? (selectedTinkoffStats.total_commission / selectedTinkoffStats.total_revenue * 100).toFixed(2) : 0}%`} />
                  <Card icon="↩️" label="Возвраты" value={fmt(selectedTinkoffStats.total_refunds)} color="#e04848" />
                  <Card icon="📅" label="Лучший день" value={fmt(selectedTinkoffStats.best_day?.revenue)} sub={selectedTinkoffStats.best_day?.date} color="#00a7dc" />
                </div>
              )}

              {/* Прогноз на конец месяца */}
              {(() => {
                const now = new Date();
                const curMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
                const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
                const daysPassed = now.getDate();
                const daysLeft = daysInMonth - daysPassed;

                const monthRows = tinkoffDaily.filter(d => d.date?.startsWith(curMonth));
                const monthRevenue = monthRows.reduce((a, b) => a + (b.revenue || 0), 0);
                const monthNet = monthRows.reduce((a, b) => a + (b.net || 0), 0);

                // Показываем только для текущего месяца; fallback на МойСклад если нет Tinkoff
                if (period !== curMonth || daysPassed < 2) return null;
                const baseRevenue = monthRevenue > 0 ? monthRevenue : (summary?.total_revenue || 0);
                if (!baseRevenue) return null;

                const dailyAvgRevenue = baseRevenue / daysPassed;
                const commRate = (monthRevenue > 0 && monthNet > 0) ? (1 - monthNet / monthRevenue) : 0.017;
                const dailyAvgNet = monthRevenue > 0 ? monthNet / daysPassed : dailyAvgRevenue * (1 - commRate);
                const projRevenue = Math.round(dailyAvgRevenue * daysInMonth);
                const projNet = Math.round(dailyAvgNet * daysInMonth);
                const projLeft = Math.round(dailyAvgRevenue * daysLeft);
                const prevRev = prevSummary?.total_revenue || 0;
                const projVsPrev = prevRev > 0 ? ((projRevenue - prevRev) / prevRev * 100).toFixed(1) : null;
                const sourceLabel = monthRevenue > 0 ? 'Т-Банк' : 'МойСклад';
                const pct = Math.round((daysPassed / daysInMonth) * 100);

                return (
                  <div style={{
                    background: 'linear-gradient(135deg, rgba(0,167,220,0.12), rgba(14,159,110,0.08))',
                    border: '1px solid rgba(0,167,220,0.35)',
                    borderRadius: 14, padding: '20px 24px', marginBottom: 20,
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--text1)' }}>📈 Прогноз на конец месяца</div>
                        <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 2 }}>
                          Прошло {daysPassed} из {daysInMonth} дней · ещё {daysLeft} дн. · темп {fmt(Math.round(dailyAvgRevenue))}/день
                        </div>
                      </div>
                      {/* Прогресс-бар месяца */}
                      <div style={{ minWidth: 140 }}>
                        <div style={{ fontSize: 11, color: 'var(--text2)', marginBottom: 4, textAlign: 'right' }}>{pct}% месяца</div>
                        <div style={{ height: 6, borderRadius: 3, background: 'var(--border)', overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: `${pct}%`, borderRadius: 3, background: 'linear-gradient(90deg, #00a7dc, #0e9f6e)', transition: 'width 0.3s' }} />
                        </div>
                      </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12 }}>
                      {/* Факт текущего месяца */}
                      <div style={{ background: 'rgba(14,159,110,0.08)', borderRadius: 10, padding: '12px 16px', border: '1px solid rgba(14,159,110,0.2)' }}>
                        <div style={{ fontSize: 11, color: 'var(--text2)', marginBottom: 4 }}>✅ Уже зачислено</div>
                        <div style={{ fontSize: 22, fontWeight: 800, color: '#0e9f6e' }}>{fmt(monthRevenue)}</div>
                        <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 2 }}>за {daysPassed} дн. текущего месяца</div>
                      </div>

                      {/* Ожидаем ещё */}
                      <div style={{ background: 'rgba(0,167,220,0.08)', borderRadius: 10, padding: '12px 16px', border: '1px solid rgba(0,167,220,0.2)' }}>
                        <div style={{ fontSize: 11, color: 'var(--text2)', marginBottom: 4 }}>⏳ Ожидаем ещё</div>
                        <div style={{ fontSize: 22, fontWeight: 800, color: '#00a7dc' }}>{fmt(projLeft)}</div>
                        <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 2 }}>за оставшиеся {daysLeft} дн.</div>
                      </div>

                      {/* Прогноз итого */}
                      <div style={{ background: 'rgba(224,138,0,0.08)', borderRadius: 10, padding: '12px 16px', border: '1px solid rgba(224,138,0,0.25)' }}>
                        <div style={{ fontSize: 11, color: 'var(--text2)', marginBottom: 4 }}>🎯 Прогноз итого</div>
                        <div style={{ fontSize: 22, fontWeight: 800, color: '#e08a00' }}>{fmt(projRevenue)}</div>
                        <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 2 }}>чистыми: {fmt(projNet)}</div>
                      </div>
                    </div>
                  </div>
                );
              })()}

              <div style={{ background: 'var(--bg2)', borderRadius: 12, padding: '20px 24px', border: '1px solid var(--border)', marginBottom: 20 }}>
                <div style={{ fontWeight: 600, marginBottom: 16 }}>📊 Дневная выручка эквайринга Т-Банк</div>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={tinkoffDaily} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                    <XAxis dataKey="label" tick={{ fontSize: 10, fill: 'var(--text2)' }} interval={Math.floor(tinkoffDaily.length / 8)} />
                    <YAxis tickFormatter={fmtShort} tick={{ fontSize: 11, fill: 'var(--text2)' }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Bar dataKey="revenue" name="Зачисления" fill="#0e9f6e" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="commission" name="Комиссия" fill="#e08a00" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="refunds" name="Возвраты" fill="#e04848" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div style={{ background: 'var(--bg2)', borderRadius: 12, padding: '20px 24px', border: '1px solid var(--border)' }}>
                <div style={{ fontWeight: 600, marginBottom: 16 }}>📋 Детализация по дням</div>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr>
                        {['Дата', 'Зачисления', 'Комиссия', 'Возвраты', 'Чистая'].map(h => (
                          <th key={h} style={{ padding: '8px 12px', textAlign: h === 'Дата' ? 'left' : 'right', color: 'var(--text2)', fontWeight: 600, borderBottom: '1px solid var(--border)' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {[...tinkoffDaily].reverse().map((row, i) => (
                        <tr key={row.date} style={{ borderBottom: '1px solid var(--border)', background: i % 2 === 0 ? 'transparent' : 'rgba(30,36,37,0.02)' }}>
                          <td style={{ padding: '7px 12px' }}>{row.date}</td>
                          <td style={{ padding: '7px 12px', textAlign: 'right', color: '#0e9f6e', fontWeight: 600 }}>{fmt(row.revenue)}</td>
                          <td style={{ padding: '7px 12px', textAlign: 'right', color: '#e08a00' }}>{fmt(row.commission)}</td>
                          <td style={{ padding: '7px 12px', textAlign: 'right', color: '#e04848' }}>{fmt(row.refunds)}</td>
                          <td style={{ padding: '7px 12px', textAlign: 'right', color: '#00a7dc', fontWeight: 600 }}>{fmt(row.net)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
